// Support assistant data (docs/v3/ASSISTANT.md 9).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AssistantChat, AssistantChatSummary, AssistantHandover, AssistantStatusDto } from "@convenor/shared/v3";
import { api, queryString } from "../../api/client";

export const assistantKeys = {
  status: (audience: string) => ["assistant", "status", audience] as const,
  chats: ["assistant", "chats"] as const,
  chat: (id: string) => ["assistant", "chat", id] as const,
};

export function useAssistantStatus(audience: "team" | "expert", fromPath: string) {
  return useQuery({
    queryKey: assistantKeys.status(audience),
    queryFn: () => api.get<AssistantStatusDto>(`/api/assistant/status${queryString({ audience, fromPath })}`),
    staleTime: 30_000,
  });
}

export function useAssistantChats(enabled: boolean) {
  return useQuery({ queryKey: assistantKeys.chats, queryFn: () => api.get<{ chats: AssistantChatSummary[] }>("/api/assistant/chats"), enabled });
}

export function useOpenChat() {
  return useMutation({ mutationFn: (id: string) => api.get<AssistantChat>(`/api/assistant/chats/${id}`) });
}

export function useDeleteChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/assistant/chats/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: assistantKeys.chats }),
  });
}

export function useFeedback() {
  return useMutation({ mutationFn: (v: { messageId: string; helpful: boolean }) => api.post<void>(`/api/assistant/messages/${v.messageId}/feedback`, { helpful: v.helpful }) });
}

export function useHandover() {
  return useMutation({ mutationFn: (chatId: string) => api.post<AssistantHandover>(`/api/assistant/chats/${chatId}/handover`, {}) });
}
